import classNames from 'classnames'
import React, { FormEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { useSignup } from '@context/SignupContext'

import { Body1, Heading6 } from '@ui/Typography'
import { Button } from '@ui/components/Button'
import { Radio } from '@ui/components/Radio'

import { getTickmillCompanyByHostname, isTickmillUKType } from '@utils/companyName.utils'
import { useWindowResize } from '@utils/domUtils'

import { AccountType } from '@model/AccountType'

import { AppMarketLink } from '@global/appMarketLink/mobile/AppMarketLink'

import { safeLocalStorage } from '@hooks/useStorage'

import { RadioGroup } from '@shared/components/RadioGroup/RadioGroup'

import { LeftSection } from '../../Components/LeftSection'
import { SignupFormWrapper } from '../../Components/SignupFormWrapper'
import { SignupSections, SignupStep } from '../../Components/SignupStep'
import { SignupWrapper } from '../../Components/SignupWrapper'

import styles from './AccountType.module.scss'

export interface FormValuesStep0 {
  typeId: AccountType
}

export const AccountTypeStep: React.FC = () => {
  const { resetSignup, signupData, appendValues: onAccountChange } = useSignup()
  const isMobile = useWindowResize()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const entity = getTickmillCompanyByHostname()
  const location = useLocation()
  const searchParams = location.search
  const accountType = signupData.values.typeId

  useEffect(() => {
    if ((!signupData.lead?.id && signupData.leadResponse?.leadId) || location?.state?.keepValues) {
      return
    }
    resetSignup()
  }, [
    location?.state?.keepValues,
    resetSignup,
    signupData.lead?.id,
    signupData.leadResponse?.leadId,
  ])

  useEffect(() => {
    safeLocalStorage.removeItem('selectedProductIdsKey')
  }, [])

  const handleSubmitStep = (event: FormEvent) => {
    event.preventDefault()
    navigate(`/sign-up/personal-details/basic${searchParams}`)
  }

  const hideMobileWrapper = !isTickmillUKType(entity) && accountType !== AccountType.Corporate

  return (
    <SignupFormWrapper label={t('Sign up.Sign up')}>
      <SignupStep
        section={SignupSections.AccountType}
        accountTypeId={signupData.values?.typeId || 1}
      />
      {isMobile && !hideMobileWrapper && (
        <SignupWrapper>
          <section className={styles.formSection}>
            <div className={styles.fewSteps}>
              <div>
                <Heading6 medium>{t('Sign up.Few steps')}</Heading6>
                <Body1 isParagraph>
                  {t('Sign up.In order to open a Tickmill Trading account you will need:')}
                </Body1>
              </div>
              <Body1>
                <Body1 isParagraph medium>
                  {t('Sign up.Personal ID')}:
                </Body1>
                <span className='text-secondary'>{t(`Sign up.Passport`)}</span>
              </Body1>
              <Body1 isParagraph>
                <Body1 isParagraph medium>
                  {t('Sign up.Proof of Address')}:
                </Body1>
                <span className='text-secondary'>{t('Sign up.Bill')}</span>
              </Body1>
              {accountType === AccountType.Corporate && (
                <Body1 isParagraph>
                  <Body1 isParagraph medium>
                    {t('Sign up.Company ID')}:
                  </Body1>
                  <span className='text-secondary'>{t('Sign up.Corporate Certificates')}</span>
                </Body1>
              )}
            </div>
          </section>
        </SignupWrapper>
      )}
      <SignupWrapper>
        {!isMobile && (
          <LeftSection
            title={t('Sign up.Few steps')}
            description={() => (
              <React.Fragment>
                <Body1 isParagraph>
                  {t('Sign up.In order to open a Tickmill Trading account you will need:')}
                </Body1>
                <Body1 isParagraph>
                  <Body1 medium>{t('Sign up.Personal ID')}: </Body1>
                  <span className='text-secondary'>{t(`Sign up.Passport`)}</span>
                </Body1>
                <Body1 isParagraph>
                  <Body1 medium>{t('Sign up.Proof of Address')}: </Body1>
                  <span className='text-secondary'>{t('Sign up.Bill')}</span>
                </Body1>
                {accountType === AccountType.Corporate && (
                  <Body1 isParagraph>
                    <Body1 isParagraph medium>
                      {t('Sign up.Company ID')}:
                    </Body1>
                    <span className='text-secondary'>{t('Sign up.Corporate Certificates')}</span>
                  </Body1>
                )}
              </React.Fragment>
            )}
            hasQRCodeSection
          />
        )}

        <section className={styles.formSection}>
          <form data-hs-do-not-collect='true' className={styles.form}>
            <Heading6 className={styles.title} medium>
              {t('Sign up.Select Account type')}
            </Heading6>
            <RadioGroup<AccountType> name='accountType' value={accountType}>
              {[
                {
                  id: AccountType.Individual,
                  name: t('Sign up.Individual'),
                },
                {
                  id: AccountType.Corporate,
                  name: t('Sign up.Corporate'),
                },
              ].map((item) => (
                <div
                  className={classNames(styles.field, {
                    [styles.isActive]: accountType === item.id,
                  })}
                >
                  <Radio
                    onChange={(val) => onAccountChange({ typeId: val })}
                    value={item.id}
                    checked={accountType === item.id}
                    name='accountType'
                  >
                    {item.name}
                  </Radio>
                </div>
              ))}
            </RadioGroup>
            <div className={styles.button}>
              <Button.Primary size='lg' fullWidth type='submit' onClick={handleSubmitStep}>
                {t('Sign up.Proceed')}
              </Button.Primary>
            </div>
          </form>
        </section>
      </SignupWrapper>
      <AppMarketLink />
    </SignupFormWrapper>
  )
}
